import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import Page from "flarum/common/components/Page";
import chatState from "../state/chat";
import ChatSidebar from "./ChatSidebar";
import ErrorBoundary from "./ErrorBoundary";
import ChannelView from "./ChannelView";
import ThreadPanel from "./ThreadPanel";
import PinnedPanel from "./PinnedPanel";
import ThreadsList from "./ThreadsList";
import BookmarksList from "./BookmarksList";
import FlaggedMessagesList from "./FlaggedMessagesList";
import ChatSearch from "./ChatSearch";
import { ChannelSkeleton } from "./Skeletons";
import { mobileTitleControl, mobileBackControl } from "../utils/toolbar";
import { isNarrowViewport } from "../utils/surface";
import { chatTitle } from "../utils/branding";

/**
 * Full-screen chat.
 *
 * Sidebar plus channel side by side on desktop; on mobile the router shows one or
 * the other, since a 280px sidebar beside a channel is unusable at phone widths.
 */
export default class ChatPage extends Page {
  constructor() {
    super(...arguments);
    /**
     * Whether the page has nothing to draw yet.
     *
     * Seeded from the state rather than hardcoded to `true`. The channel list is
     * fetched once per session and kept current by realtime, so on every visit
     * after the first the data is already in hand — starting at `true` threw it
     * away and showed a skeleton over a list that was ready to render, which is
     * the flash that made re-entering the chat look like a reload.
     */
    _defineProperty(this, "loading", !chatState.channelsLoaded);
    /** What was last handed to `app.history`; see `recordHistory`. */
    _defineProperty(this, "historyKey", null);
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    document.body.classList.add(ChatPage.BODY_CLASS);
  }
  oninit(vnode) {
    super.oninit(vnode);

    // Something in the tab bar while the page boots; `recordHistory` replaces it
    // with the section's or the channel's own name as soon as it knows one.
    app.setTitle(chatTitle());

    // The drawer and the page are mutually exclusive views of the same state.
    //
    // Deliberately not `setDrawerOpen(false)`: that clears any pending suspension,
    // which would throw away the very thing that tells us to reopen the drawer on
    // the way out. Arriving here from the nav button leaves `drawerSuspended` false
    // anyway, so nothing is restored for a visit that did not come from the drawer.
    chatState.hideDrawerForPage();
    this.boot();
  }

  /**
   * Fires when the chat is left for the rest of the forum — not when moving between
   * chat routes, which share one mounted page (see ChatPageResolver).
   */
  onremove(vnode) {
    super.onremove(vnode);

    // The rest of the forum gets its footer back.
    document.body.classList.remove(ChatPage.BODY_CLASS);

    // Puts back a drawer that only stepped aside for this page. A drawer the user
    // closed with the X is not suspended, so it stays closed.
    if (chatState.resumeDrawer()) {
      // The full-screen page owns the thread panel through the URL; the drawer has
      // no route to read it back from, and reopening into a thread the user cannot
      // navigate out of would strand them. The pinned list and the search share
      // that slot, so all three go — a drawer coming back should come back to the
      // conversation, not to whatever was over it when the page took over.
      chatState.closeOverlays();
      chatState.setDrawerCollapsed(false);
    }
  }
  onbeforeupdate(vnode) {
    super.onbeforeupdate(vnode);
    this.syncFromRoute();
    this.recordHistory();
  }

  /**
   * Registers the current chat route with core's history stack.
   *
   * This is what puts a back button in the phone toolbar. Core mounts its
   * `Navigation` into `#app-navigation` and that component shows the back arrow
   * only when `app.history.canGoBack()` — otherwise it shows the drawer toggle.
   * `ForumApplication.mount()` seeds the stack with one entry (the index), and
   * every core page pushes its own on arrival: `IndexPage`, `DiscussionPage`,
   * `UserPage`, `NotificationsPage`, and outside core `TagsPage` and
   * flarum/messages' `MessagesPage`. This page never did, so the stack stayed one
   * deep on every chat route and the toolbar had nothing but the hamburger — on
   * `/chat`, `/chat/threads`, `/chat/bookmarks`, `/chat/search` and `/chat/flags`
   * alike.
   *
   * Three names rather than one, because the name is what core uses to decide
   * whether a visit is a new step or a replacement (`History#push` overwrites an
   * entry of the same name). Distinct names for the list, a channel and a section
   * make "back" walk channel → list → forum, which is the order the user arrived
   * in. One shared name would collapse all three into a single step and send them
   * from a channel straight out of the chat.
   *
   * Called from `onbeforeupdate` as well as `oninit` because every chat route
   * shares one mounted page (see ChatPageResolver): moving between them is an
   * update, not a create. The key guard keeps that to one push per actual change
   * rather than one per redraw.
   */
  recordHistory() {
    var _m$route$get;
    const routeName = this.attrs.routeName;
    const channel = chatState.channel(chatState.activeChannelId);
    const section = ChatPage.isSectionRoute(routeName);

    // Nothing is filed until the page knows what it is showing. A channel route
    // whose channel has not arrived yet — a deep link, or the first frame after
    // `oninit` — would otherwise be filed under the *list's* name, since that is
    // all the state can report at that point, and "back" from the channel would
    // then reload the channel. Both waits end in a redraw, which brings us
    // straight back here.
    if (this.loading) return;
    if (m.route.param("id") && !channel) return;
    const name = section ? "chat.section" : channel ? "chat.channel" : "chat";

    // The channel's own name only when the channel is what is on screen; a
    // section is named after itself even with a channel still selected behind it.
    const title = !section && channel ? channel.displayName() : this.toolbarTitle(routeName);
    const url = (_m$route$get = m.route.get()) != null ? _m$route$get : "";
    const key = "".concat(name, "\0").concat(title, "\0").concat(url);
    if (key === this.historyKey) return;
    this.historyKey = key;

    // Together, because they answer the same question: `setTitle` is the browser
    // tab, `push` is the back button. Setting only the first — which is what this
    // page did — leaves the tab reading "Chat" on a page called "Bookmarks".
    app.setTitle(title);
    app.history.push(name, title, url);
  }

  /**
   * Brings the active channel and thread back in line with the URL.
   *
   * Every chat route shares one mounted page (see ChatPageResolver), so `oninit`
   * runs once and cannot be where the route is read. In-app navigation sets the
   * state before it routes, which makes this a no-op most of the time; it exists
   * for the paths that do not — browser back and forward, and a link arriving from
   * outside the page.
   */
  syncFromRoute() {
    const routeId = m.route.param("id");
    const channelId = routeId ? Number(routeId) : null;

    // Only when the route actually names one. `/chat/search` and `/chat/threads`
    // carry no id and must not be read as "no channel selected" — the search pane
    // keeps its channel context.
    if (channelId !== null && channelId !== chatState.activeChannelId) {
      chatState.setActiveChannel(channelId);

      // Deep link to a channel the sidebar has not loaded.
      if (!chatState.channel(channelId)) {
        app.store.find("chat-channels", String(channelId)).catch(() => {});
      }
    }
    const threadParam = m.route.param("threadId");
    const threadId = threadParam ? Number(threadParam) : null;
    if (threadId !== chatState.activeThreadId) {
      chatState.activeThreadId = threadId;
    }
  }
  view() {
    const channel = chatState.channel(chatState.activeChannelId);
    const narrow = isNarrowViewport();
    const threadId = chatState.activeThreadId;

    // `routeName` is supplied by core's DefaultResolver. Several routes resolve to
    // this same component, so the main pane is chosen from it rather than from the
    // path — the sidebar is shared by all of them.
    const routeName = this.attrs.routeName;

    // Exactly one pane on a phone, never two.
    //
    // The stylesheet gives both `flex: 1 1 100%` at this width, on the assumption
    // that only one of them is ever mounted. That held for an open channel — the
    // sidebar was dropped — but not for `/chat` with nothing selected, nor for the
    // section routes: both panes rendered, split the row in half, and the result
    // was a 50%-wide channel list beside a 50%-wide "you have not joined any
    // channels yet". Which is the squeezed sidebar this fixes.
    const section = ChatPage.isSectionRoute(routeName);
    const showsMain = narrow ? Boolean(channel) || section : true;
    const showsSidebar = narrow ? !showsMain : true;
    return m("div", {
      className: "ChatPage"
    }, mobileTitleControl(this.toolbarTitle(routeName)), narrow && section ? mobileBackControl(() => this.deselect()) : null, showsSidebar ? m(ErrorBoundary, {
      area: "sidebar"
    }, m(ChatSidebar, {
      state: chatState,
      onSelect: c => this.select(c)
    })) : null, showsMain ? m("div", {
      className: "ChatPage-main"
    }, m(ErrorBoundary, {
      area: "page"
    }, this.mainPane(routeName, channel, narrow, threadId))) : null);
  }

  /**
   * Whether the route names one of the sidebar's sections rather than a channel.
   *
   * These fill the main pane on their own — a section is shown whether or not a
   * channel happens to be selected — and unlike a channel they render no header,
   * so on a phone they are also the routes that need a back control put in the
   * toolbar for them.
   */
  static isSectionRoute(routeName) {
    return routeName === "chat.search" || routeName === "chat.threads" || routeName === "chat.bookmarks" || routeName === "chat.flags";
  }

  /**
   * What the phone toolbar calls this page.
   *
   * Keyed off the route rather than the open channel, for the reason given on
   * `mobileTitleControl`. The strings are the sidebar's own, so a section is named
   * the same in the bar as in the list it came from and no new translations are
   * introduced for installs that have already been translated.
   */
  toolbarTitle(routeName) {
    const sections = {
      "chat.threads": "my_threads",
      "chat.bookmarks": "bookmarks",
      "chat.search": "search",
      "chat.flags": "flags"
    };
    const section = routeName ? sections[routeName] : undefined;
    if (section) {
      return app.translator.trans("ramon-chat.forum.sidebar.".concat(section), {}, true);
    }

    // The channel list and any channel open on top of it. `chatTitle()` rather
    // than the translation directly, so an admin who renamed the feature sees the
    // new name here too.
    return chatTitle();
  }
  mainPane(routeName, channel, narrow, threadId) {
    // The pane's real shape, not a spinner: this is the entire right-hand side
    // of the page on first load.
    if (this.loading) return ChannelSkeleton();
    if (routeName === "chat.search") {
      // The channel button passes `?channel=`; the sidebar link does not, and
      // then the search spans every channel the actor can read.
      const scope = m.route.param("channel");
      return m(ChatSearch, {
        state: chatState,
        channelId: scope ? Number(scope) : null
      });
    }
    if (routeName === "chat.threads") {
      return m(ThreadsList, {
        state: chatState
      });
    }
    if (routeName === "chat.bookmarks") {
      return m(BookmarksList, {
        state: chatState
      });
    }
    if (routeName === "chat.flags") {
      return m(FlaggedMessagesList, {
        state: chatState
      });
    }

    // Built as an array rather than a JSX fragment with a conditional slot.
    // Mithril's normalizeChildren decides a fragment is keyed from its first child
    // and then requires every other child to be keyed too — and it counts a `null`
    // as unkeyed. So `[<ChannelView key=…/>, null]`, which is what an absent thread
    // panel produced, threw "In fragments, vnodes must either all have keys or none
    // have keys" and took the page down. Pushing only what is actually rendered
    // keeps every entry present and keyed.
    const panes = [channel ? m(ChannelView, {
      key: "channel-".concat(channel.id()),
      channel: channel,
      state: chatState,
      onBack: narrow ? () => this.deselect() : undefined
    }) : this.empty()];

    // Beside the channel on desktop; the panel's own stylesheet takes it full-bleed
    // over the channel below the mobile breakpoint. The thread and pinned panels
    // share the slot and are mutually exclusive — see ChatState.togglePinned().
    if (channel && threadId) {
      panes.push(m(ThreadPanel, {
        key: "thread-".concat(threadId),
        channel: channel,
        threadId: threadId,
        state: chatState,
        onClose: () => this.closeThread(channel)
      }));
    } else if (channel && chatState.showPinned) {
      panes.push(m(PinnedPanel, {
        key: "pinned-".concat(channel.id()),
        channel: channel,
        state: chatState,
        onClose: () => {
          chatState.showPinned = false;
          m.redraw();
        }
      }));
    }
    return panes;
  }
  empty() {
    // Keyed because it shares the pane array with the keyed channel view.
    return m("div", {
      className: "ChatBrowse-empty",
      key: "empty"
    }, app.translator.trans("ramon-chat.forum.sidebar.no_channels"));
  }
  async boot() {
    try {
      await Promise.all([chatState.loadChannels(), chatState.loadDrafts()]);
      const routeId = m.route.param("id");
      if (routeId) {
        chatState.setActiveChannel(Number(routeId));

        // Deep link to a channel that is not in the sidebar (not joined, or the
        // list is truncated): fetch it directly rather than showing "empty".
        if (!chatState.channel(chatState.activeChannelId)) {
          try {
            await app.store.find("chat-channels", String(routeId));
          } catch (_unused) {
            chatState.setActiveChannel(null);
          }
        }
      } else if (!chatState.activeChannelId) {
        chatState.setActiveChannel(chatState.channels[0] ? Number(chatState.channels[0].id()) : null);
      }
      const threadId = m.route.param("threadId");
      chatState.activeThreadId = threadId ? Number(threadId) : null;
    } finally {
      this.loading = false;
      m.redraw();
    }
  }
  select(channel) {
    chatState.setActiveChannel(Number(channel.id()));
    m.route.set(app.route("chat.channel", {
      id: channel.id()
    }));
  }
  deselect() {
    chatState.setActiveChannel(null);
    m.route.set(app.route("chat.index"));
  }

  /**
   * Closing the panel also drops the thread from the URL, so a reload does not
   * reopen what was just dismissed.
   */
  closeThread(channel) {
    chatState.closeThread();
    m.route.set(app.route("chat.channel", {
      id: channel.id()
    }));
  }
}
/**
 * Marks `<body>` while this page is mounted, so the stylesheet can suppress
 * whatever else the forum draws around it.
 *
 * Core's `Page.bodyClass` puts its class on `#app`, which is not enough: the
 * things this page has to hide are appended to `document.body` as siblings of
 * `#app` — `huseyinfiliz/modern-footer` mounts `<footer id="modern-footer">`
 * there from an `app.mount` extender, and it is not the only extension that
 * does. A class on `#app` cannot select an element outside `#app`.
 */
_defineProperty(ChatPage, "BODY_CLASS", "ChatFullPage");
flarum.reg.add('ramon-chat', 'forum/components/ChatPage', ChatPage);